import { EditOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import { isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatPhone } from "../../../_src_shared/utils/functions/formatPhone";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { detailSidebarClear } from "../../../redux/reducers/detailSidebarReducer";
import SidebarTabSwitcher from "../../borrowerDetails/shared/SidebarTabSwitcher";
import JsonPreview from "../../common/JsonPreview";
import TabContent from "../../navigation/TabContent";
import SidebarBodyContent from "../components/SidebarBodyContent";
import ContentArea from "../shared/ContentArea";
import DetailRow from "../shared/DetailRow";
import SidebarSection from "../shared/SidebarSection";
import {
  FIELD_TYPE,
  countVisibleFields,
} from "../shared/sidebarFieldUtils";
import ResizeableSidebar from "../ResizeableSidebar";
import { PanelIconButton } from "../shared/panelChrome";
import ContactEditForm from "./ContactEditForm";

const TABS = {
  DETAILS: "Details",
  JSON: "JSON",
};

const LoanRelatedContactSidebar = ({ contact }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanId = loanDrilldown?._id;

  const [primaryTab, setPrimaryTab] = useState(TABS.DETAILS);
  const [isEditing, setIsEditing] = useState(false);

  const handleClose = () => {
    setPrimaryTab(TABS.DETAILS);
    setIsEditing(false);
    dispatch(detailSidebarClear());
  };

  const handleStartEdit = useCallback(() => setIsEditing(true), []);
  const handleCancelEdit = useCallback(() => setIsEditing(false), []);
  const handleEditSuccess = useCallback(() => setIsEditing(false), []);

  const TAB_LIST = useMemo(
    () => [
      { label: TABS.DETAILS, value: TABS.DETAILS },
      { label: TABS.JSON, value: TABS.JSON },
    ],
    [],
  );

  if (isNil(contact)) return null;

  const fullName =
    `${contact?.firstName || ""} ${contact?.lastName || ""}`.trim() ||
    "Contact";
  const description =
    [contact?.loanRole, contact?.company].filter(Boolean).join(" • ") ||
    undefined;

  return (
    <ResizeableSidebar
      open
      hideBackdrop
      onClose={handleClose}
      title={fullName}
      description={description}
      bodySx={{ padding: "0 !important" }}
      // Opens at the shared panel width (panelChrome.PANEL_DEFAULT_WIDTH).
      headerEndContent={
        !isEditing && (
          <PanelIconButton
            title="Edit Contact"
            icon={EditOutlined}
            onClick={handleStartEdit}
          />
        )
      }
    >
      {isEditing ? (
        <SidebarBodyContent py={0} px={0}>
          <ContactEditForm
            contact={contact}
            loanId={loanId}
            onClose={handleCancelEdit}
            onSuccess={handleEditSuccess}
          />
        </SidebarBodyContent>
      ) : (
        <>
          <SidebarTabSwitcher
            tabs={TAB_LIST}
            value={primaryTab}
            onChange={setPrimaryTab}
          />
          <SidebarBodyContent py={0} px={0}>
            <TabContent tabKey={TABS.DETAILS} activeTab={primaryTab}>
              <ContactDetailsTab
                contact={contact}
                onStartEdit={handleStartEdit}
              />
            </TabContent>
            <TabContent tabKey={TABS.JSON} activeTab={primaryTab}>
              <ContentArea>
                <JsonPreview values={contact} defaultCollapse={3} />
              </ContentArea>
            </TabContent>
          </SidebarBodyContent>
        </>
      )}
    </ResizeableSidebar>
  );
};

const ContactDetailsTab = ({ contact, onStartEdit }) => {
  const identityFields = useMemo(
    () => [
      {
        label: "First Name",
        value: contact?.firstName,
        fieldType: FIELD_TYPE.EDITABLE,
      },
      {
        label: "Middle Name",
        value: contact?.middleName,
        fieldType: FIELD_TYPE.EDITABLE,
      },
      {
        label: "Last Name",
        value: contact?.lastName,
        fieldType: FIELD_TYPE.EDITABLE,
      },
      {
        label: "Loan Role",
        value: contact?.loanRole,
        fieldType: FIELD_TYPE.EDITABLE,
      },
      {
        label: "Company",
        value: contact?.company,
        fieldType: FIELD_TYPE.EDITABLE,
      },
      ...(contact?.loanRole === "Appraiser"
        ? [
            {
              label: "Appraiser License #",
              value: contact?.appraiserLicenseNumber,
              fieldType: FIELD_TYPE.EDITABLE,
            },
          ]
        : []),
    ],
    [contact],
  );

  const contactFields = useMemo(
    () => [
      {
        label: "Email",
        value: contact?.emailAddress,
        fieldType: FIELD_TYPE.EDITABLE,
      },
      {
        label: "Phone",
        value: contact?.phone ? formatPhone(contact.phone) : null,
        fieldType: FIELD_TYPE.EDITABLE,
      },
    ],
    [contact],
  );

  const metaFields = useMemo(
    () => [
      {
        label: "Created",
        value: contact?.createDateTime
          ? formatDateTime(contact.createDateTime, "datetime")
          : null,
        fieldType: FIELD_TYPE.READ_ONLY,
      },
      {
        label: "Profile Complete",
        value:
          contact?.profileComplete === true
            ? "Yes"
            : contact?.profileComplete === false
              ? "No"
              : null,
        fieldType: FIELD_TYPE.READ_ONLY,
      },
    ],
    [contact],
  );

  return (
    <ContentArea px={1.5}>
      <Box sx={{ mb: 0 }} />
      <FieldsSection
        title="Identity"
        fields={identityFields}
        onEdit={onStartEdit}
      />
      <FieldsSection
        title="Contact"
        fields={contactFields}
        onEdit={onStartEdit}
      />
      <FieldsSection title="Metadata" fields={metaFields} />
    </ContentArea>
  );
};

const FieldsSection = ({ title, fields, onEdit }) => {
  const { visible, total } = useMemo(
    () => countVisibleFields(fields, null),
    [fields],
  );
  if (visible === 0) return null;
  return (
    <SidebarSection
      title={title}
      visibleCount={visible}
      totalCount={total}
      onEdit={onEdit}
    >
      {fields.map((f) => (
        <DetailRow
          key={f.label}
          label={f.label}
          value={f.value}
          editable={f.fieldType === FIELD_TYPE.EDITABLE}
          computed={f.fieldType === FIELD_TYPE.COMPUTED}
          fieldType={f.fieldType}
        />
      ))}
    </SidebarSection>
  );
};

export default LoanRelatedContactSidebar;
